<template>
  <!--
    Shared toast card used by both the priority alert carousel
    (MultipleNotifications.vue) and the regular alert stack (Notification.vue).
    The visual language is the "order fill" trade-app style: a lifted slate
    card with a compact neutral title, a bold primary line of order details,
    an optional timestamp, and a circular dismiss in the corner. Severity is
    communicated through the slim accent bar on the left edge while title and
    detail text stay neutral so every toast reads with the same calm weight.
  -->
  <div
    class="alert-card"
    :class="[`alert-card--${variant}`, { 'alert-card--clickable': clickable }]"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    @click="handleClick"
    @keyup.enter="handleClick"
  >
    <div class="alert-card__content">
      <div class="alert-card__text">
        <div class="alert-card__title-row">
          <span
            class="alert-card__title"
            :class="{ 'alert-card__nav': navigable }"
            @click="handleNavClick"
          >
            {{ title }}
            <font-awesome-icon
              v-if="navigable"
              :icon="faArrowRightLong"
              class="alert-card__nav-arrow"
            />
          </span>
          <!-- Fill-progress chip, e.g. "6%". Only present on order-fill toasts
               that carry a parseable filled/total fraction. -->
          <span v-if="percent != null" class="alert-card__percent">
            {{ percent }}%
          </span>
          <span v-if="metaText || time" class="alert-card__meta">
            <span
              v-if="metaText"
              class="alert-card__matchup"
              :class="{ 'alert-card__nav': navigable }"
              @click="handleNavClick"
              >{{ metaText }}</span
            >
            <template v-if="metaText && time"> · </template>
            <template v-if="time">{{ time }}</template>
          </span>
        </div>
        <span v-if="detail" class="alert-card__detail">{{ detail }}</span>
      </div>
    </div>
    <button
      v-if="dismissible"
      type="button"
      class="alert-card__dismiss"
      :aria-label="dismissLabel"
      @click.stop="handleDismiss"
    >
      <font-awesome-icon :icon="faXmark" />
    </button>
  </div>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons/faArrowRightLong';

const props = defineProps({
  title: { type: String, required: true },
  detail: { type: String, default: '' },
  // Optional fill-progress percent (0–100) shown as a small chip in the title
  // row. null/undefined hides the chip entirely.
  percent: { type: Number, default: null },
  time: { type: String, default: '' },
  variant: {
    type: String,
    default: 'neutral',
    validator: (v) =>
      ['neutral', 'success', 'warning', 'cancel', 'danger'].includes(v),
  },
  clickable: { type: Boolean, default: false },
  dismissible: { type: Boolean, default: true },
  dismissLabel: { type: String, default: 'Dismiss notification' },
  // Optional meta in the title row after the time (e.g. the matchup "LAL @ BOS").
  // The parent only supplies this when it adds context the detail line lacks —
  // e.g. totals ("Over 220.5" doesn't say which game).
  metaText: { type: String, default: '' },
  // When true the title + matchup become the navigation affordance (trailing
  // arrow + hover underline). Clicking that text emits 'navigate'; clicking
  // anywhere else on the card just emits 'click' (ack). The parent owns both.
  navigable: { type: Boolean, default: false },
});

const emit = defineEmits(['click', 'dismiss', 'navigate']);

function handleClick() {
  emit('click');
}

// Clicks on the navigable text (title/matchup) route instead of acking. We
// stop propagation so the card-level click (ack) doesn't also fire. When the
// card isn't navigable the click is allowed to bubble up and ack like any
// other part of the card.
function handleNavClick(event) {
  if (!props.navigable) return;
  event.stopPropagation();
  emit('navigate');
}

function handleDismiss() {
  emit('dismiss');
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.alert-card {
  // Each variant sets --accent-color; neutral leaves it transparent so the
  // accent bar disappears. We render the bar via an inset box-shadow rather
  // than a left border so the inner padding stays aligned across variants.
  --accent-color: transparent;

  position: relative;
  z-index: 1;
  width: clamp(280px, calc(100vw - 32px), 380px);
  padding: 14px 18px;
  background: #f8f8fb;
  border: 1px solid rgba(39, 45, 88, 0.05);
  border-radius: 12px;
  box-shadow:
    inset 3px 0 0 var(--accent-color),
    0 6px 18px rgba(15, 23, 42, 0.07);
  color: #111827;
  text-align: left;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;

  // Dark mode: lifted slate card on the deep navy app background, with a
  // soft luminous border, near-white main line, and a heavier shadow that
  // reads cleanly against the dark surface.
  [theme='night'] & {
    background: #2a3052;
    border-color: rgba(150, 156, 203, 0.12);
    box-shadow:
      inset 3px 0 0 var(--accent-color),
      0 10px 24px rgba(0, 0, 0, 0.45);
    color: #ffffff;
  }

  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    padding: 12px 14px;
    border-radius: 10px;
  }

  &--clickable {
    cursor: pointer;

    // Hover keeps the accent bar (via --accent-color) and lifts the shadow.
    &:hover {
      transform: translateY(-1px);
      box-shadow:
        inset 3px 0 0 var(--accent-color),
        0 10px 24px rgba(15, 23, 42, 0.1);

      [theme='night'] & {
        box-shadow:
          inset 3px 0 0 var(--accent-color),
          0 10px 24px rgba(0, 0, 0, 0.45);
      }
    }

    &:focus-visible {
      outline: 2px solid theme.$color-primary;
      outline-offset: 2px;
    }
  }

  &__content {
    display: flex;
    align-items: center;
    // Reserve room on the right for the absolutely-positioned close button.
    padding-right: 24px;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }

  &__title {
    flex: 0 1 auto;
    font-weight: theme.$font-weight-semibold;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #2b2b32;
    overflow-wrap: anywhere;

    [theme='night'] & {
      color: #e0e3f0;
    }
  }

  &__detail {
    font-weight: theme.$font-weight-semibold;
    font-size: 15px;
    line-height: 1.3;
    color: #111827;
    display: block;
    letter-spacing: -0.005em;
    overflow-wrap: anywhere;

    @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
      font-size: 14px;
    }

    [theme='night'] & {
      color: #ffffff;
    }
  }

  // Fill-progress chip in the title row, e.g. "6%". Compact pill that reads as
  // an at-a-glance "how much of the offer is filled" without the risk/win
  // ambiguity of the old bare "(12/200)".
  &__percent {
    flex-shrink: 0;
    align-self: center;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: theme.$font-weight-bold;
    line-height: 1.4;
    letter-spacing: 0.02em;
    color: theme.$color-primary;
    background: rgba(theme.$color-primary, 0.12);
    white-space: nowrap;

    [theme='night'] & {
      color: #8ea2ff;
      background: rgba(142, 162, 255, 0.16);
    }
  }

  // Meta cluster: matchup · time. Sits right next to the title, muted and
  // compact. Matchup (only present for totals) disambiguates which game.
  &__meta {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: theme.$font-weight-normal;
    letter-spacing: 0.02em;
    color: #9a9aa3;
    white-space: nowrap;

    [theme='night'] & {
      color: #969ccb;
    }
  }

  // Navigable affordance shared by the title and the matchup: pointer + hover
  // underline so they read as "click to view game". The click itself bubbles
  // to the card's own click handler, which acks + routes.
  &__nav {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  // Long right arrow trailing the title on navigable fills. Nudges right when
  // the title is hovered for a little polish.
  &__nav-arrow {
    margin-left: 5px;
    font-size: 11px;
    vertical-align: middle;
    color: theme.$color-primary;
    transition: transform 0.15s ease;

    .alert-card__title:hover & {
      transform: translateX(2px);
    }

    [theme='night'] & {
      color: #8ea2ff;
    }
  }

  // Close: light gray circular button, intentionally subdued.
  &__dismiss {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    padding: 0;
    border: none;
    background: rgba(15, 23, 42, 0.05);
    color: #6b7280;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 9px;
    line-height: 1;
    transition:
      background 0.15s ease,
      color 0.15s ease;

    &:hover {
      background: rgba(15, 23, 42, 0.1);
      color: #111827;
    }

    &:focus-visible {
      outline: 2px solid theme.$color-primary;
      outline-offset: 1px;
    }

    @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
      right: 10px;
      width: 16px;
      height: 16px;
      font-size: 8px;
    }

    [theme='night'] & {
      background: rgba(255, 255, 255, 0.06);
      color: #c5c8e6;

      &:hover {
        background: rgba(255, 255, 255, 0.12);
        color: #ffffff;
      }
    }
  }

  // Variants only set --accent-color (drives the inset left bar). Title and
  // detail text intentionally stay neutral across every variant so the
  // accent bar alone communicates severity.

  &--success {
    --accent-color: #10b981;

    [theme='night'] & {
      --accent-color: #34d399;
    }
  }

  &--warning,
  &--cancel {
    --accent-color: #d97706;

    [theme='night'] & {
      --accent-color: #fbbf24;
    }
  }

  &--danger {
    --accent-color: #dc2626;

    [theme='night'] & {
      --accent-color: #f87171;
    }
  }
}
</style>
